import React from 'react';
import { Check, X } from 'lucide-react';
import { Button } from './ui/button';

const DetailedServices = ({ onContactClick }) => {
  const pricingPackages = [
    {
      id: 1,
      name: "STARTER",
      price: "£99",
      period: "mo",
      subtitle: "For brand new businesses",
      features: [
        { name: "1 Social Platform Managed", included: true },
        { name: "8 Posts Per Month", included: true },
        { name: "Basic Content Calendar", included: true },
        { name: "Monthly Engagement Report", included: true },
        { name: "Canva Graphics (branded)", included: true },
        { name: "Caption Copywriting", included: true },
        { name: "Paid Ad Management", included: false },
        { name: "Video Editing", included: false },
        { name: "SEO Support", included: false },
        { name: "Priority Support", included: false }
      ],
      popular: false
    },
    {
      id: 2,
      name: "BASIC",
      price: "£150",
      period: "mo",
      subtitle: "For growing freelancers",
      features: [
        { name: "2 Social Platforms Managed", included: true },
        { name: "12 Posts Per Month", included: true },
        { name: "Full Content Calendar", included: true },
        { name: "Monthly Analytics Report", included: true },
        { name: "Branded Graphics + Flyers", included: true },
        { name: "Caption & Hashtag Strategy", included: true },
        { name: "Limited Facebook Ads", included: true },
        { name: "Email Marketing Templates", included: true },
        { name: "Video Editing", included: false },
        { name: "Priority Support", included: false }
      ],
      popular: true
    },
    {
      id: 3,
      name: "ADVANCED",
      price: "£255",
      period: "mo",
      subtitle: "For established clients",
      features: [
        { name: "3 Social Platforms Managed", included: true },
        { name: "20 Posts Per Month", included: true },
        { name: "Full Content Calendar", included: true },
        { name: "Bi-Weekly Analytics Reports", included: true },
        { name: "Full Content Creation (Photo/Video)", included: true },
        { name: "Ad Campaign Optimisation", included: true },
        { name: "Advanced Market Insights", included: true },
        { name: "Email Campaign Management", included: true },
        { name: "Basic SEO + Blog Support", included: true },
        { name: "Priority Support (48hr response)", included: true }
      ],
      popular: false
    },
    {
      id: 4,
      name: "PREMIUM",
      price: "£450",
      period: "mo",
      subtitle: "Full-service growth",
      features: [
        { name: "All Platforms Managed", included: true },
        { name: "Unlimited Posts", included: true },
        { name: "Full Content Calendar", included: true },
        { name: "Weekly Performance Reviews", included: true },
        { name: "Full Video + Professional Production", included: true },
        { name: "Multi-Platform Campaign Orchestration", included: true },
        { name: "Competitor + Trend Analysis", included: true },
        { name: "Full Email Marketing Suite", included: true },
        { name: "SEO + Website Blog Management", included: true },
        { name: "VIP Support (24hr response)", included: true }
      ],
      popular: false
    }
  ];

  const addOnServices = [
    {
      id: 1,
      service: "Website Build (Basic)",
      price: "£250 one-off",
      description: "5-page WordPress/Wix site with brand styling"
    },
    {
      id: 2,
      service: "Website Maintenance",
      price: "£50/month",
      description: "Updates, security, hosting support"
    },
    {
      id: 3,
      service: "Professional Video Package",
      price: "£100/video",
      description: "Edited TikTok/Reel with captions & music"
    },
    {
      id: 4,
      service: "Extra Social Posts",
      price: "£15/post",
      description: "Additional posts beyond package limit"
    },
    {
      id: 5,
      service: "Custom Graphic Design",
      price: "£40/design",
      description: "Logos, infographics, marketing materials"
    }
  ];

  return (
    <section className="py-24 bg-brand-brown">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Target Demographics */}
          <div className="mb-20">
            <h2 className="text-4xl font-bold text-white mb-8">TARGET DEMOGRAPHICS</h2>
            <div className="bg-[#2a1519] rounded-xl p-8 border border-brand-olive/30">
              <ul className="space-y-4 text-brand-cream/90">
                <li className="flex items-start gap-3">
                  <span className="text-brand-yellow mt-1">•</span>
                  <span><strong>Age:</strong> 18–45, UK-based (with potential to expand internationally)</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand-yellow mt-1">•</span>
                  <span><strong>Revenue stage:</strong> Pre-revenue to £50k/year turnover</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand-yellow mt-1">•</span>
                  <span><strong>Platforms:</strong> Instagram, TikTok, YouTube, Facebook, LinkedIn</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand-yellow mt-1">•</span>
                  <span><strong>Budget sensitivity:</strong> Value-conscious — seeking premium quality at fair prices</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Services & Pricing Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              SERVICES & PRICING PACKAGES
            </h2>
            <p className="text-xl text-brand-cream/80 max-w-3xl mx-auto">
              All packages are monthly retainers. Clients are onboarded with a discovery call and brand audit before work begins. 
              Packages are reviewed quarterly to reflect scope changes or upsell opportunities.
            </p>
          </div>

          {/* Pricing Grid */}
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6 mb-20">
            {pricingPackages.map((pkg) => (
              <div 
                key={pkg.id}
                className={`relative rounded-2xl p-6 transition-all duration-300 hover:scale-105 ${
                  pkg.popular 
                    ? 'bg-gradient-to-br from-brand-olive to-brand-olive/90 text-white shadow-2xl border-4 border-brand-yellow' 
                    : 'bg-[#2a1519] border-2 border-brand-olive/30 shadow-lg hover:shadow-xl'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-brand-yellow text-brand-brown px-4 py-1 rounded-full text-sm font-bold">
                      MOST POPULAR
                    </div>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className={`text-xl font-bold mb-2 ${pkg.popular ? 'text-white' : 'text-brand-yellow'}`}>
                    {pkg.name}
                  </h3>
                  <div className="mb-2">
                    <span className={`text-4xl font-bold ${pkg.popular ? 'text-white' : 'text-brand-yellow'}`}>
                      {pkg.price}
                    </span>
                    <span className={`text-lg ${pkg.popular ? 'text-white/80' : 'text-brand-cream/70'}`}>
                      /{pkg.period}
                    </span>
                  </div>
                  <p className={`text-sm italic ${pkg.popular ? 'text-white/90' : 'text-brand-cream/60'}`}>
                    {pkg.subtitle}
                  </p>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          pkg.popular ? 'text-brand-yellow' : 'text-brand-olive'
                        }`} />
                      ) : (
                        <X className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          pkg.popular ? 'text-white/40' : 'text-brand-cream/30'
                        }`} />
                      )}
                      <span className={`text-sm ${
                        (() => {
                          if (feature.included) {
                            return pkg.popular ? 'text-white' : 'text-brand-cream/90';
                          }
                          return pkg.popular ? 'text-white/40 line-through' : 'text-brand-cream/40 line-through';
                        })()
                      }`}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  onClick={onContactClick}
                  className={`w-full py-6 font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-white text-brand-olive hover:bg-brand-yellow hover:text-brand-brown' 
                      : 'bg-brand-olive text-white hover:bg-brand-yellow hover:text-brand-brown'
                  }`}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>

          {/* Add-Ons Section */}
          <div className="mt-20">
            <h2 className="text-4xl font-bold text-white mb-8">ADD-ONS & SCALABLE SERVICES</h2>
            <div className="bg-[#2a1519] rounded-2xl overflow-hidden border border-brand-olive/30">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-brown/60">
                    <tr>
                      <th className="text-left py-4 px-6 text-brand-yellow font-bold">Add-On Service</th>
                      <th className="text-left py-4 px-6 text-brand-yellow font-bold">Price</th>
                      <th className="text-left py-4 px-6 text-brand-yellow font-bold">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {addOnServices.map((addon, index) => (
                      <tr 
                        key={addon.id}
                        className={`${index !== addOnServices.length - 1 ? 'border-b border-brand-olive/20' : ''} hover:bg-brand-olive/10 transition-colors duration-200`}
                      >
                        <td className="py-4 px-6 text-white font-semibold">{addon.service}</td>
                        <td className="py-4 px-6 text-brand-yellow font-bold">{addon.price}</td>
                        <td className="py-4 px-6 text-brand-cream/80">{addon.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center p-12 bg-gradient-to-r from-brand-olive/20 to-brand-yellow/20 rounded-2xl border border-brand-yellow/30">
            <h3 className="text-3xl font-bold text-white mb-4">Ready to Choose Your Package?</h3>
            <p className="text-brand-cream/80 mb-6 max-w-2xl mx-auto">
              Book a free discovery call to discuss which package best fits your business goals.
            </p>
            <Button 
              onClick={onContactClick}
              size="lg"
              className="bg-brand-yellow hover:bg-brand-yellow/90 text-brand-brown font-bold px-12 py-6 text-xl transition-all duration-300 hover:scale-105"
            >
              Schedule Discovery Call
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DetailedServices;
